import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import {
  Apple,
  Calculator,
  Calendar,
  MessageSquare,
  Target,
  TrendingUp,
  Crown,
  Lock,
  Sparkles,
  Utensils,
  Scale,
  Camera,
  Activity,
  Heart,
  ArrowRight,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const AIDietitian = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [goals, setGoals] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [statusRes, goalsRes] = await Promise.all([
        axios.get(`${API}/ai/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ai/goals`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { has_goals: false } }))
      ]);

      setStatus(statusRes.data);
      setIsPremium(statusRes.data?.is_premium === true);
      setGoals(goalsRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Calculator,
      title: 'Kalori Hesaplama',
      description: 'Günlük kalori ve makro ihtiyacınızı hesaplayın',
      link: '/ai-dietitian/calorie-check',
      premium: true
    },
    {
      icon: Camera,
      title: 'Yemek Fotoğrafı Analizi',
      description: 'Tabağınızın fotoğrafını çekin, kalori analizi alın',
      link: '/ai-dietitian/food-analyzer',
      premium: true,
      isNew: true
    },
    {
      icon: Calendar,
      title: 'Haftalık Beslenme Planı',
      description: 'AI destekli kişiselleştirilmiş beslenme planı',
      link: '/ai-dietitian/weekly-plan',
      premium: true
    },
    {
      icon: MessageSquare,
      title: 'Diyetisyene Sor',
      description: 'Beslenme hakkında sorularınızı sorun',
      link: '/ai-dietitian/chat',
      premium: true
    },
    {
      icon: Target,
      title: 'Hedef Takibi',
      description: 'Kilo, yağ oranı ve kas gelişiminizi takip edin',
      link: '/ai-dietitian/goals',
      premium: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen pt-20 wellness-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // NON-PREMIUM USER: Show upgrade prompt
  if (!isPremium) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-b from-green-50 via-white to-green-50">
        <SEO {...SEO_CONFIG.aiDietitian} />
        {/* Hero Section */}
        <section className="py-12 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white/20 p-3 rounded-full inline-block mb-4">
              <Apple className="h-10 w-10" />
            </div>
            <h1 className="text-4xl font-bold mb-4">AI Diyetisyen</h1>
            <p className="text-white/90 text-lg max-w-2xl mx-auto">
              Yapay zeka destekli kişisel beslenme asistanınız
            </p>
          </div>
        </section>

        <div className="max-w-3xl mx-auto px-4 py-12">
          {/* Premium Required Alert */}
          <Card className="mb-8 border-yellow-400 bg-yellow-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="bg-yellow-200 p-3 rounded-full">
                  <Lock className="h-6 w-6 text-yellow-700" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                    Premium Üyelik Gerekli
                  </h3>
                  <p className="text-yellow-700 mb-4">
                    AI Diyetisyen özellikleri sadece Premium üyelere açıktır. 
                    Kişiselleştirilmiş beslenme planları, kalori hesaplama, yemek analizi ve daha fazlası için Premium&apos;a yükseltin.
                  </p>
                  <Button
                    onClick={() => navigate('/premium')}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                  >
                    <Crown className="h-4 w-4 mr-2" />
                    Premium&apos;a Yükselt
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feature Preview (Locked) */}
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Premium Özellikler</h2>
          <div className="grid gap-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="opacity-75 cursor-not-allowed">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="bg-gray-100 p-3 rounded-full">
                      <Icon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-600 flex items-center gap-2">
                        {feature.title}
                        {feature.isNew && (
                          <Badge className="bg-purple-500 text-white text-xs">Yeni</Badge>
                        )}
                      </h3>
                      <p className="text-sm text-gray-400">{feature.description}</p>
                    </div>
                    <Lock className="h-5 w-5 text-gray-400" />
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA */}
          <Card className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
            <CardContent className="p-8 text-center">
              <Crown className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Premium AI Diyetisyen</h3>
              <p className="text-gray-600 mb-6 max-w-xl mx-auto">
                Sınırsız kalori hesaplama, haftalık beslenme planları, kişiselleştirilmiş tarif önerileri ve daha fazlası!
              </p>
              <ul className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
                <li className="flex items-center text-green-700">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Sınırsız beslenme planı
                </li>
                <li className="flex items-center text-green-700">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Sınırsız sohbet
                </li>
                <li className="flex items-center text-green-700">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Hedef takibi
                </li>
                <li className="flex items-center text-green-700">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Yemek fotoğrafı analizi
                </li>
              </ul>
              <Button
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                onClick={() => navigate('/premium')}
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Premium&apos;a Yükselt
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // PREMIUM USER: Show full features
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-green-50 via-white to-green-50">
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Apple className="h-10 w-10" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-center mb-4">AI Diyetisyen Premium</h2>
          <p className="text-center text-white/90 text-lg max-w-2xl mx-auto">
            Yapay zeka destekli kişisel beslenme asistanınız. Türk mutfağına uygun, sağlıklı ve lezzetli öneriler.
          </p>
          
          {/* Premium Status */}
          <div className="flex justify-center mt-6">
            <Badge className="bg-yellow-500 text-yellow-900 px-4 py-2 text-sm">
              <Crown className="h-4 w-4 mr-2" />
              Premium Üye - Sınırsız Erişim
            </Badge>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Stats */}
        {goals?.has_goals && (
          <Card className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-green-800 flex items-center">
                    <Scale className="h-5 w-5 mr-2" />
                    Mevcut Durum
                  </h3>
                  <p className="text-green-700">
                    {goals.goals?.current_weight} kg → Hedef: {goals.goals?.target_weight} kg
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-sm text-green-600">Kalan</span>
                    <p className="text-2xl font-bold text-green-800">
                      {Math.abs(goals.goals?.current_weight - goals.goals?.target_weight).toFixed(1)} kg
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="border-green-500 text-green-700"
                    onClick={() => navigate('/ai-dietitian/goals')}
                  >
                    İlerlemeyi Gör
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            
            return (
              <Card
                key={index}
                className="relative hover:shadow-lg transition-all cursor-pointer"
                onClick={() => navigate(feature.link)}
              >
                <CardHeader>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 bg-green-100">
                    <Icon className="h-7 w-7 text-green-600" />
                  </div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                    {feature.isNew && (
                      <Badge className="bg-purple-500 text-white text-xs">Yeni</Badge>
                    )}
                  </div>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Sınırsız</span>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(feature.link);
                      }}
                    >
                      Başla <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AIDietitian;
